import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import {
  ShoppingBag, Heart, Bookmark, Bell, Loader2, Sparkles,
} from "lucide-react";
import ProfileHeader from "@/components/profile/ProfileHeader";
import { ProfileHighlightsRow } from "@/components/feed/StoryStudioExtras";
import { AnonymizeDialog } from "@/components/media/AnonymizeDialog";
import ImageCropDialog from "@/components/home/ImageCropDialog";
import { downscaleImage } from "@/lib/imageDownscale";
import STXProfileCard from "@/components/profile/STXProfileCard";
import ExclusiveTabContent from "@/components/profile/ExclusiveTabContent";
import FollowListModal from "@/components/profile/FollowListModal";
import FeedCard from "@/components/feed/FeedCard";
import { TabBar } from "@/components/toggles";

export default function ProfilePage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [caps, setCaps] = useState([]);
  const [personId, setPersonId] = useState(null);
  const [presence, setPresence] = useState({ SOCIAL: false, MARKETPLACE: false, STILLTOUCHEDX: false });

  const [socialData, setSocialData] = useState(null);
  const [stxData, setStxData] = useState(null);
  const [socialPosts, setSocialPosts] = useState([]);
  const [stxListings, setStxListings] = useState([]);
  const [mpListings, setMpListings] = useState([]);

  const [activeArea, setActiveArea] = useState("SOCIAL");
  const [socialTab, setSocialTab] = useState("posts");
  const [followModal, setFollowModal] = useState(null); // {type}

  const getCap = (code) => caps.find((c) => c.capability_code === code);
  const [watchListingUnread, setWatchListingUnread] = useState(0);
  const [watchPostUnread, setWatchPostUnread] = useState(0);

  // iter326 · Kamera-Schnellbearbeitung von Avatar/Cover direkt auf dem eigenen Profil.
  // Nutzt dieselben Endpoints wie „Profil bearbeiten": Upload via Anonymisierung → PATCH /profile.
  const quickFileRef = useRef(null);
  const [quickTarget, setQuickTarget] = useState(null); // "avatar" | "banner"
  const [quickCropFile, setQuickCropFile] = useState(null);
  const [quickAnonFile, setQuickAnonFile] = useState(null);
  const openQuickEdit = (target) => { setQuickTarget(target); quickFileRef.current?.click(); };
  const onQuickFilePicked = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    // Erst verkleinern (Handy/HEIC), dann Zuschnitt im passenden Seitenverhältnis.
    setQuickCropFile(await downscaleImage(f));
  };
  const onQuickCropped = (cropped) => {
    const isBanner = quickTarget === "banner";
    setQuickCropFile(null);
    setQuickAnonFile(new File([cropped], isBanner ? "cover.jpg" : "avatar.jpg", { type: cropped.type || "image/jpeg" }));
  };
  const onQuickAnonComplete = async (finalUrl) => {
    const isBanner = quickTarget === "banner";
    setQuickAnonFile(null);
    try {
      const newUrl = `${finalUrl}?v=${Date.now()}`;
      await api.patch("/profile", isBanner ? { banner_url: newUrl } : { avatar_url: newUrl });
      setSocialData((prev) => (prev ? { ...prev, [isBanner ? "banner_url" : "avatar_url"]: newUrl } : prev));
      if (refreshUser) { try { await refreshUser(); } catch { /* ignore */ } }
      window.dispatchEvent(new CustomEvent("profile-updated"));
    } catch { /* ignore */ }
    finally { setQuickTarget(null); }
  };

  useEffect(() => {
    if (!user || user === false) { navigate("/login"); return; }
    (async () => {
      try {
        const { data: own } = await api.get("/profile");
        setCaps(own.capabilities || []);
        setPersonId(own.person_id);
        const handle = own.handle;
        const [socialRes, presRes] = await Promise.all([
          api.get(`/profiles/${handle}`).catch(() => ({ data: { ...own, handle } })),
          api.get(`/profiles/${handle}/area-presence`).catch(() => ({ data: { presence: {} } })),
        ]);
        setSocialData(socialRes.data);
        setPresence(presRes.data.presence || {});
      } catch {
        navigate("/onboarding/profile");
      } finally {
        setLoading(false);
      }
    })();
  }, [user, navigate]);

  useEffect(() => {
    if (!user || user === false) return;
    const load = async () => {
      try {
        const { data } = await api.get("/me/watch-notifications/summary");
        const btt = data.by_target_type || {};
        setWatchListingUnread(btt.LISTING || 0);
        setWatchPostUnread(btt.POST || 0);
      } catch { /* ignore */ }
    };
    load();
    const onRefresh = () => load();
    window.addEventListener("watch-unread-refresh", onRefresh);
    return () => window.removeEventListener("watch-unread-refresh", onRefresh);
  }, [user]);

  const hasStudio = getCap("ST_CREATOR")?.status === "ACTIVE" || getCap("MP_SELLER")?.status === "ACTIVE" || getCap("STX_SELLER")?.status === "ACTIVE";

  const areas = ["SOCIAL"];
  if (presence.STILLTOUCHEDX) areas.push("STILLTOUCHEDX");
  if (presence.MARKETPLACE) areas.push("MARKETPLACE");

  useEffect(() => {
    if (activeArea !== "SOCIAL" || !personId || socialTab !== "posts") return;
    (async () => {
      try {
        const { data } = await api.get(`/home/feed`);
        setSocialPosts((data.posts || []).filter(
          (p) => p.author_id === personId && p.author_type !== "PLATFORM" && !p?.author?.is_platform
        ));
      } catch { setSocialPosts([]); }
    })();
  }, [activeArea, personId, socialTab]);

  useEffect(() => {
    if (activeArea !== "STILLTOUCHEDX" || stxData) return;
    const stxHandle = socialData?.canonical_stx_handle;
    if (!stxHandle) return;
    (async () => {
      try {
        const [{ data: p }, { data: l }] = await Promise.all([
          api.get(`/profiles/${stxHandle}`),
          api.get(`/profiles/${stxHandle}/listings?area=STILLTOUCHEDX`).catch(() => ({ data: { listings: [] } })),
        ]);
        setStxData(p);
        setStxListings(l.listings || []);
      } catch { /* ignore */ }
    })();
  }, [activeArea, stxData, socialData]);

  useEffect(() => {
    if (activeArea !== "MARKETPLACE" || !socialData?.handle) return;
    (async () => {
      try {
        const { data } = await api.get(`/profiles/${socialData.handle}/listings?area=MARKETPLACE`);
        setMpListings(data.listings || []);
      } catch { setMpListings([]); }
    })();
  }, [activeArea, socialData]);

  if (loading || !socialData) {
    return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#999]" /></div>;
  }

  const publicCount = Number(socialData.public_post_count ?? socialData.post_count ?? 0);
  const exclusiveCount = Number(socialData.exclusive_post_count ?? 0);
  const socialTabs = [{ key: "posts", label: `${t("public_profile.posts", { defaultValue: "Beiträge" })} (${publicCount})` }];
  if (socialData.has_exclusive_content) socialTabs.push({ key: "exklusiv", label: `${t("public_profile.exclusive", { defaultValue: "Exklusiv" })} (${exclusiveCount})` });

  const onCountClick = (type) => setFollowModal({ type });

  const QuickActions = () => {
    const items = [
      { icon: ShoppingBag, label: t("profile.my_orders", { defaultValue: "Meine Bestellungen" }), path: "/profile/purchases", testId: "nav-purchases" },
      { icon: Bookmark, label: t("profile_page.saved_posts", { defaultValue: "Gespeicherte Beiträge" }), path: "/saved-posts", testId: "nav-saved-posts", badge: watchPostUnread },
      { icon: Bell, label: t("profile_page.saved_searches", { defaultValue: "Gespeicherte Suchen" }), path: "/search-alerts", testId: "nav-search-alerts" },
      { icon: Heart, label: t("profile_page.saved_articles", { defaultValue: "Gespeicherte Artikel" }), path: "/watchlist", testId: "nav-watchlist", badge: watchListingUnread },
    ];
    return (
      <div data-testid="profile-quick-actions" className="grid grid-cols-4 gap-2 px-3 py-3 bg-white rounded-2xl border border-[#EEE] mx-3 my-3">
        {items.map(({ icon: Icon, label, path, testId, badge }) => (
          <button key={path} data-testid={testId} onClick={() => navigate(path)}
            className="relative flex flex-col items-center gap-1.5 py-2 rounded-xl hover:bg-[#FAFAFA] transition-colors">
            <div className="relative">
              <Icon size={20} className="text-[#0A0A0A]" />
              {badge > 0 && <span className="absolute -top-1.5 -right-2 min-w-4 h-4 px-1 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">{badge > 9 ? "9+" : badge}</span>}
            </div>
            <span className="text-[10px] leading-tight text-center text-[#444] font-medium">{label}</span>
          </button>
        ))}
      </div>
    );
  };

  return (
    <div data-testid="profile-page" className="pb-6 w-full max-w-3xl 2xl:max-w-full 2xl:px-6 mx-auto 2xl:mx-0">
      {/* ===== Top-Leiste: Umschalter-Pills + Studio auf einer Zeile ===== */}
      <div className="flex items-center gap-2 px-4 pt-4">
        {areas.length > 1 ? (
          <div data-testid="profile-area-switcher" className="min-w-0 overflow-x-auto no-scrollbar">
            <div className="inline-flex p-1 rounded-full bg-[#F0F0F0] gap-0.5">
              {areas.map((a) => (
                <button key={a} data-testid={`profile-area-tab-${a.toLowerCase()}`} onClick={() => setActiveArea(a)}
                  className={`px-3 h-7 rounded-full text-[11px] font-semibold whitespace-nowrap transition-colors ${activeArea === a ? "bg-[#0A0A0A] text-white" : "text-[#666] hover:text-[#0A0A0A]"}`}>
                  {a === "SOCIAL" ? "Social" : a === "STILLTOUCHEDX" ? "stilltouchedX" : "Marketplace"}
                </button>
              ))}
            </div>
          </div>
        ) : <span className="flex-1" />}

        <button data-testid={hasStudio ? "studio-cta" : "studio-unlock-cta"}
          onClick={() => navigate(hasStudio ? "/studio" : "/profile/access")}
          className="shrink-0 ml-auto h-7 px-3 rounded-full bg-[#0A0A0A] text-white text-[11px] font-semibold flex items-center gap-1 hover:bg-[#222] transition-colors active:scale-[0.98]">
          <Sparkles size={12} /> {hasStudio ? t("profile.studio", { defaultValue: "Studio" }) : t("profile.studio_unlock_short", { defaultValue: "Freischalten" })}
        </button>
      </div>

      {/* ===== Profil-Ansicht je Bereich ===== */}
      {activeArea === "SOCIAL" && (
        <div data-testid="profile-view-social">
          <ProfileHeader profile={socialData} area="SOCIAL" itemCount={publicCount} isOwn presence={{}} navigate={navigate} theme="light" onCountClick={onCountClick} onEditPhoto={openQuickEdit} />
          <ProfileHighlightsRow profileId={personId} />
          <QuickActions />
          <TabBar tabs={socialTabs.map((td) => ({ key: td.key, label: td.label, testId: `profile-tab-${td.key}` }))} activeKey={socialTab} onChange={setSocialTab} />
          {socialTab === "posts" ? (
            <div data-testid="profile-posts">
              {socialPosts.length === 0 ? (
                <p className="px-4 py-12 text-center text-sm text-[#999]">{t("public_profile.no_posts", { defaultValue: "Noch keine Beiträge." })}</p>
              ) : socialPosts.map((p) => <FeedCard key={p.post_id} post={p} />)}
            </div>
          ) : (
            <ExclusiveTabContent profile={socialData} navigate={navigate} user={user} />
          )}
        </div>
      )}

      {activeArea === "STILLTOUCHEDX" && (
        <div data-testid="profile-view-stx" className="bg-stx-bg text-stx-text rounded-b-2xl">
          {!stxData ? (
            <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-stx-accent" /></div>
          ) : (
            <>
              <ProfileHeader profile={stxData} area="STILLTOUCHEDX" itemCount={stxListings.length} isOwn presence={{}} navigate={navigate} theme="dark" onCountClick={onCountClick} />
              <QuickActions />
              {stxData.seller_facets && Object.keys(stxData.seller_facets).some((k) => stxData.seller_facets[k]) && (
                <STXProfileCard facets={stxData.seller_facets} />
              )}
              <div className="px-5 pt-4 pb-3 flex items-center justify-between border-t border-stx-border">
                <h2 className="text-[10px] font-bold tracking-[0.2em] text-stx-text-dim inline-flex items-center gap-1.5">
                  <Sparkles size={11} className="text-stx-accent" /> {t("seller_profile_extra.articles_stx_headline", { defaultValue: "STX-ARTIKEL" })}
                </h2>
                <span className="text-[10px] text-stx-text-dim tabular-nums">{stxListings.length}</span>
              </div>
              {stxListings.length === 0 ? (
                <div className="px-5 py-14 text-center text-sm text-stx-text-muted">{t("seller_profile_extra.no_articles_stx", { defaultValue: "Noch keine STX-Artikel." })}</div>
              ) : (
                <div data-testid="profile-stx-grid" className="grid gap-3 px-4 pb-6" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))" }}>
                  {stxListings.map((l) => (
                    <button key={l.listing_id} data-testid={`profile-stx-listing-${l.listing_id}`} onClick={() => navigate(`/stilltouchedx/${l.listing_id}`)}
                      className="group rounded-xl overflow-hidden bg-stx-panel border border-stx-border hover:border-stx-accent transition-all text-left">
                      <div className="relative aspect-[3/4] overflow-hidden bg-stx-panel-2">
                        {l.media_urls?.[0] ? <img src={l.media_urls[0]} alt="" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" loading="lazy" /> : <div className="w-full h-full flex items-center justify-center text-stx-text-dim"><Heart size={20} /></div>}
                      </div>
                      <div className="p-2.5">
                        <p className="text-xs text-stx-text truncate leading-tight">{l.title}</p>
                        <p className="text-sm font-bold mt-1 text-stx-accent tabular-nums">{l.price?.toFixed(2)} <span className="text-stx-text-muted text-[10px]">{l.currency}</span></p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {activeArea === "MARKETPLACE" && (
        <div data-testid="profile-view-marketplace">
          <ProfileHeader profile={socialData} area="MARKETPLACE" itemCount={mpListings.length} isOwn presence={{}} navigate={navigate} theme="light" onCountClick={onCountClick} onEditPhoto={openQuickEdit} />
          <QuickActions />
          <h2 className="px-4 pt-1 pb-2 text-[10px] font-bold uppercase tracking-wider text-[#999]">{t("seller_profile_extra.articles_mp", { defaultValue: "Artikel" })}</h2>
          {mpListings.length === 0 ? (
            <div className="px-4 py-12 text-center text-sm text-[#999]">{t("seller_profile_extra.no_articles_mp", { defaultValue: "Noch keine Artikel." })}</div>
          ) : (
            <div data-testid="profile-mp-grid" className="grid gap-3 p-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))" }}>
              {mpListings.map((l) => (
                <button key={l.listing_id} data-testid={`profile-mp-listing-${l.listing_id}`} onClick={() => navigate(`/marketplace/${l.listing_id}`)} className="flex flex-col text-left rounded-2xl overflow-hidden border border-[#E5E5E5] hover:border-[#0A0A0A] transition-colors bg-white">
                  <div className="relative aspect-[3/4] overflow-hidden bg-[#F5F5F5]">
                    {l.media_urls?.[0] ? <img src={l.media_urls[0]} alt="" className="w-full h-full object-cover" loading="lazy" /> : <div className="w-full h-full flex items-center justify-center opacity-30"><Heart size={18} /></div>}
                  </div>
                  <div className="p-2">
                    <p className="text-xs font-medium truncate leading-tight">{l.title}</p>
                    <p className="text-sm font-bold mt-0.5 text-[#0A0A0A]">{l.price?.toFixed(2)} {l.currency}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <FollowListModal
        open={!!followModal}
        type={followModal?.type}
        area={activeArea}
        onClose={() => setFollowModal(null)}
      />

      {/* iter326 · Kamera-Schnellbearbeitung: Datei wählen → Anonymisierung → PATCH /profile */}
      <input
        ref={quickFileRef}
        type="file"
        accept="image/*"
        className="hidden"
        data-testid="profile-quick-edit-input"
        onChange={onQuickFilePicked}
      />
      {quickCropFile && quickTarget && (
        <ImageCropDialog
          file={quickCropFile}
          aspect={quickTarget === "banner" ? 16 / 9 : 1}
          title={quickTarget === "banner"
            ? t("edit_profile_extra.cover_crop_title", { defaultValue: "Coverbild zuschneiden" })
            : t("edit_profile_extra.avatar_crop_title", { defaultValue: "Avatar zuschneiden" })}
          onCancel={() => { setQuickCropFile(null); setQuickTarget(null); }}
          onCropped={onQuickCropped}
        />
      )}
      {quickAnonFile && quickTarget && (
        <AnonymizeDialog
          file={quickAnonFile}
          context={quickTarget === "banner" ? "PROFILE_COVER" : "PROFILE_AVATAR"}
          onCancel={() => { setQuickAnonFile(null); setQuickTarget(null); }}
          onComplete={onQuickAnonComplete}
        />
      )}
    </div>
  );
}
